import {
  CircularProgress,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import { FC, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { MuiButton } from "@ryde/controls";

import {
  DashboardChartType,
  TDashboardCardSource,
} from "../../dynamic-dashboard.models";
import {
  CARD_SOURCE_OPTIONS,
  CHART_TYPE_OPTIONS,
} from "./dashboard-card-editor.constants";
import { CardEditorFilters } from "./dashboard-card-editor.filters";
import { EditorList } from "./dashboard-card-editor.list";
import { ICardEditorFieldsProps } from "./dashboard-card-editor.models";
import { useDashboardCardEditor } from "./use-dashboard-card-editor";

/** Presentational card-editor form — all logic lives in `useDashboardCardEditor`. */
export const CardEditorFields: FC<ICardEditorFieldsProps> = ({
  cardId,
  onClose,
  onDirtyChange,
}) => {
  const { t } = useTranslation();
  const {
    cardSource,
    setCardSource,
    systemCardOptions,
    systemCardName,
    setSystemCardName,
    systemFilterSchema,
    chartableReports,
    reportId,
    setReportId,
    name,
    handleNameChange,
    chartType,
    setChartType,
    size,
    setSize,
    filterSchema,
    cardFilters,
    singleValueFilterKeys,
    addCardFilter,
    setCardFilterValues,
    removeCardFilter,
    loadingReport,
    groupingParamsExpected,
    dimensionOptions,
    metricOptions,
    seriesOptions,
    dimensionKey,
    setDimensionKey,
    metricKey,
    setMetricKey,
    seriesKey,
    setSeriesKey,
    isMultiSeries,
    isSaveDisabled,
    isDirty,
    handleSave,
  } = useDashboardCardEditor(cardId, onClose);

  const isSystemSource = cardSource === "system";
  const isNumber = chartType === DashboardChartType.Stat;

  // The panel owns the draft, but the modal owns closing — hand the flag up so a
  // backdrop click or Escape can prompt before discarding edits.
  useEffect(() => {
    onDirtyChange(isDirty);
  }, [isDirty, onDirtyChange]);

  const renderLists = (): JSX.Element => {
    if (typeof reportId !== "number") {
      return (
        <div className="lists-hint">
          {t("dashboard.dynamic.selectReportHint")}
        </div>
      );
    }
    if (loadingReport) {
      return (
        <div className="lists-hint">
          <CircularProgress size={22} />
        </div>
      );
    }
    // Grouping-expected reports get their metrics from the regroup fetch — keep
    // the spinner until it resolves instead of flashing "not chartable".
    if (groupingParamsExpected && !metricOptions.length) {
      return (
        <div className="lists-hint">
          <CircularProgress size={22} />
        </div>
      );
    }
    if (!dimensionOptions.length || !metricOptions.length) {
      return (
        <div className="lists-hint">{t("dashboard.dynamic.notChartable")}</div>
      );
    }
    // A number shows one value, so there is nothing to break it down by — offer
    // the metric alone. The grouping still happens under the hood (the analytic
    // reports only return metric columns once something is grouped) but the total
    // is the same whichever dimension is used, so it isn't the user's choice.
    if (isNumber) {
      return (
        <div className="lists">
          <EditorList
            label={t("dashboard.dynamic.metric")}
            options={metricOptions}
            selected={metricKey}
            onSelect={setMetricKey}
          />
        </div>
      );
    }

    return (
      <div className="lists">
        <EditorList
          label={t("dashboard.dynamic.groupBy")}
          options={dimensionOptions}
          selected={dimensionKey}
          onSelect={setDimensionKey}
        />
        {isMultiSeries && (
          <EditorList
            label={t("dashboard.dynamic.seriesBy")}
            options={seriesOptions}
            selected={seriesKey}
            onSelect={setSeriesKey}
          />
        )}
        <EditorList
          label={t("dashboard.dynamic.metric")}
          options={metricOptions}
          selected={metricKey}
          onSelect={setMetricKey}
        />
      </div>
    );
  };

  // A ready-made card picks a card; a report card picks a report, chart type and
  // columns. Everything else — source, name, size, filters, save — is shared, so
  // only the middle differs rather than the whole form.
  return (
    <>
      <Select
        value={cardSource}
        onChange={event =>
          setCardSource(event.target.value as TDashboardCardSource)
        }
        size="small"
        fullWidth
      >
        {CARD_SOURCE_OPTIONS.map(option => (
          <MenuItem key={option.value} value={option.value}>
            {t(option.labelKey)}
          </MenuItem>
        ))}
      </Select>

      {!isSystemSource && (
        <Select
          value={reportId === "" ? "" : String(reportId)}
          onChange={(event: SelectChangeEvent<string>) =>
            setReportId(
              event.target.value === "" ? "" : Number(event.target.value),
            )
          }
          size="small"
          fullWidth
          displayEmpty
        >
          <MenuItem value="" disabled>
            {t("dashboard.dynamic.selectReport")}
          </MenuItem>
          {chartableReports.map(report => (
            <MenuItem key={report.id} value={String(report.id)}>
              {t(report.labelKey)}
            </MenuItem>
          ))}
        </Select>
      )}

      {/*
        No name field for a ready-made card: its header renders
        `dashboard.<CARD>.TITLE`, so a typed name would be ignored — and storing
        one would freeze the card's title in whichever language it was saved.
      */}
      {!isSystemSource && (
        <TextField
          value={name}
          onChange={handleNameChange}
          size="small"
          fullWidth
          placeholder={t("dashboard.dynamic.chartName")}
        />
      )}

      {!isSystemSource && (
        <Select
          value={chartType}
          onChange={event =>
            setChartType(event.target.value as DashboardChartType)
          }
          size="small"
          fullWidth
        >
          {CHART_TYPE_OPTIONS.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {t(option.labelKey)}
            </MenuItem>
          ))}
        </Select>
      )}

      <div className="size-toggle">
        <MuiButton
          muiVariant={size === "narrow" ? "contained" : "outlined"}
          onClick={() => setSize("narrow")}
        >
          {t("dashboard.dynamic.narrow")}
        </MuiButton>
        <MuiButton
          muiVariant={size === "wide" ? "contained" : "outlined"}
          onClick={() => setSize("wide")}
        >
          {t("dashboard.dynamic.wide")}
        </MuiButton>
      </div>

      {isSystemSource ? (
        <div className="lists">
          <EditorList
            label={t("dashboard.dynamic.selectSystemCard")}
            options={systemCardOptions}
            selected={systemCardName}
            onSelect={setSystemCardName}
          />
        </div>
      ) : (
        renderLists()
      )}

      <CardEditorFilters
        schema={isSystemSource ? systemFilterSchema : filterSchema}
        values={cardFilters}
        singleValueFilterKeys={singleValueFilterKeys}
        addCardFilter={addCardFilter}
        setCardFilterValues={setCardFilterValues}
        removeCardFilter={removeCardFilter}
      />

      <div className="footer">
        <MuiButton disabled={isSaveDisabled} onClick={handleSave}>
          {t("general.SAVE")}
        </MuiButton>
      </div>
    </>
  );
};
